import React, { useEffect, useState } from "react";
import {
  Sparkles,
  Loader2,
  Save,
  Trash2,
  Calendar,
  RefreshCw,
  ScrollText,
  Users,
  User,
  TrendingUp,
} from "lucide-react";
import { dayRecapApi } from "../api";
import { useAuth } from "../AuthContext";

const todayStr = () => new Date().toISOString().slice(0, 10);

export default function DayRecap() {
  const { user } = useAuth();
  const isBoss = (user?.role || "").toLowerCase() === "boss";

  // Tab: "mine" (everyone) or "team" (boss-only)
  const [tab, setTab] = useState("mine");

  // ── My Day state ────────────────────────────────────────────────
  const [date, setDate] = useState(todayStr());
  const [draft, setDraft] = useState(null);
  const [editedSummary, setEditedSummary] = useState("");
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState([]);
  const [activeSavedId, setActiveSavedId] = useState(null);

  const loadSaved = async () => {
    try { setSaved(await dayRecapApi.list()); } catch { /* noop */ }
  };
  useEffect(() => { loadSaved(); }, []);

  const generate = async () => {
    setGenerating(true);
    setDraft(null);
    setEditedSummary("");
    setActiveSavedId(null);
    try {
      const r = await dayRecapApi.generate(date);
      setDraft(r);
      setEditedSummary(r.summary || "");
    } catch (e) {
      alert("Failed to generate recap: " + (e?.response?.data?.detail || e.message));
    } finally {
      setGenerating(false);
    }
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const r = await dayRecapApi.save({
        date: draft.date,
        summary: editedSummary || draft.summary,
        activities: draft.activities,
      });
      setSaved((s) => {
        const without = s.filter((x) => x.id !== r.id);
        return [r, ...without].sort((a, b) => b.date.localeCompare(a.date));
      });
      setActiveSavedId(r.id);
      setDraft(null);
    } catch (e) {
      alert("Save failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const removeSaved = async (id) => {
    if (!confirm("Delete this day recap?")) return;
    try {
      await dayRecapApi.remove(id);
      setSaved((s) => s.filter((x) => x.id !== id));
      if (activeSavedId === id) setActiveSavedId(null);
    } catch (e) {
      alert("Delete failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  // ── Team Recap state (boss-only) ────────────────────────────────
  const [teamDate, setTeamDate] = useState(todayStr());
  const [teamAi, setTeamAi] = useState(false);
  const [teamData, setTeamData] = useState(null);
  const [teamLoading, setTeamLoading] = useState(false);
  const [expanded, setExpanded] = useState({}); // address → bool

  const loadTeam = async () => {
    setTeamLoading(true);
    try {
      const r = await dayRecapApi.team(teamDate, teamAi);
      setTeamData(r);
    } catch (e) {
      alert("Failed to load team recap: " + (e?.response?.data?.detail || e.message));
    } finally {
      setTeamLoading(false);
    }
  };
  useEffect(() => {
    if (tab === "team" && isBoss && !teamData) loadTeam();
    // eslint-disable-next-line
  }, [tab, isBoss]);

  const activeSaved = saved.find((s) => s.id === activeSavedId);
  const shown = draft || activeSaved;

  return (
    <div className="h-full flex flex-col" data-testid="app-day-recap">
      {/* Tab strip (only show Team tab if boss) */}
      {isBoss && (
        <div className="px-3 pt-2 border-b border-white/10 flex items-center gap-1">
          <button
            data-testid="recap-tab-mine"
            onClick={() => setTab("mine")}
            className={`px-3 py-1.5 mono text-[10px] uppercase tracking-[0.28em] flex items-center gap-1.5 transition ${
              tab === "mine" ? "text-[#a78bfa] border-b-2 border-[#a78bfa]" : "text-white/45 hover:text-white/75"
            }`}
          >
            <User size={11} /> My Day
          </button>
          <button
            data-testid="recap-tab-team"
            onClick={() => setTab("team")}
            className={`px-3 py-1.5 mono text-[10px] uppercase tracking-[0.28em] flex items-center gap-1.5 transition ${
              tab === "team" ? "text-[#3b82f6] border-b-2 border-[#3b82f6]" : "text-white/45 hover:text-white/75"
            }`}
          >
            <Users size={11} /> Team Recap
          </button>
        </div>
      )}

      {tab === "mine" && (
        <div className="flex-1 flex min-h-0">
          {/* Left rail — saved recaps */}
          <aside className="w-72 border-r border-white/10 flex flex-col">
            <div className="px-3 py-2 border-b border-white/10 flex items-center justify-between">
              <span className="mono text-[10px] uppercase tracking-[0.3em] text-white/60">// recaps</span>
              <span className="mono text-[10px] text-white/40">{saved.length}</span>
            </div>
            <div className="flex-1 overflow-y-auto">
              {saved.length === 0 && (
                <div className="px-3 py-6 text-xs text-white/40 text-center">
                  No saved recaps yet. Generate today&apos;s, then click <b>Save</b>.
                </div>
              )}
              {saved.map((r) => (
                <button
                  key={r.id}
                  data-testid={`recap-item-${r.id}`}
                  onClick={() => { setActiveSavedId(r.id); setDraft(null); setEditedSummary(""); }}
                  className={`w-full text-left px-3 py-2 border-b border-white/5 hover:bg-white/5 ${activeSavedId === r.id ? "bg-white/5" : ""}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="mono text-[11px] text-[#a78bfa]">{r.date}</span>
                    <Trash2
                      size={11}
                      className="text-red-400 opacity-0 group-hover:opacity-100 hover:opacity-100"
                      onClick={(e) => { e.stopPropagation(); removeSaved(r.id); }}
                    />
                  </div>
                  <div className="text-[11px] text-white/70 line-clamp-2 mt-0.5">
                    {(r.summary || "").slice(0, 110)}
                  </div>
                </button>
              ))}
            </div>
          </aside>

          {/* Main */}
          <main className="flex-1 flex flex-col min-w-0">
            <div className="px-4 py-3 border-b border-white/10 flex items-center gap-3 flex-wrap">
              <ScrollText size={16} className="text-[#a78bfa]" />
              <div className="mono text-xs uppercase tracking-[0.2em] text-white/80">Day Recap</div>
              <div className="flex items-center gap-2 ml-2">
                <Calendar size={12} className="text-white/40" />
                <input
                  data-testid="recap-date"
                  type="date"
                  value={date}
                  max={todayStr()}
                  onChange={(e) => setDate(e.target.value)}
                  className="bg-transparent border border-white/15 rounded px-2 py-1 text-xs"
                />
              </div>
              <button
                data-testid="recap-generate"
                onClick={generate}
                disabled={generating}
                className="btn-ghost text-xs flex items-center gap-1.5 px-3 py-1.5"
                style={{ border: "1px solid rgba(139,92,246,0.45)", color: "#c7c1ff" }}
              >
                {generating ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                {generating ? "Summarizing…" : "Generate"}
              </button>
              {draft && (
                <button
                  data-testid="recap-save"
                  onClick={save}
                  disabled={saving || !editedSummary.trim()}
                  className="btn-ghost text-xs flex items-center gap-1.5 px-3 py-1.5"
                  style={{ border: "1px solid rgba(59,130,246,0.5)", color: "#bfdbfe" }}
                >
                  {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                  Save
                </button>
              )}
              {activeSaved && !draft && (
                <button
                  data-testid="recap-regenerate"
                  onClick={() => { setDate(activeSaved.date); generate(); }}
                  className="btn-ghost text-xs flex items-center gap-1.5 px-3 py-1.5"
                >
                  <RefreshCw size={12} /> Regenerate
                </button>
              )}
              {activeSaved && (
                <button
                  data-testid="recap-delete-active"
                  onClick={() => removeSaved(activeSaved.id)}
                  className="btn-ghost text-xs flex items-center gap-1.5 px-3 py-1.5 text-red-300"
                  style={{ border: "1px solid rgba(248,113,113,0.4)" }}
                >
                  <Trash2 size={12} /> Delete
                </button>
              )}
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              {!shown && !generating && (
                <div className="h-full grid place-items-center text-center">
                  <div className="max-w-md">
                    <ScrollText size={40} strokeWidth={1} className="mx-auto text-[#a78bfa] mb-3" />
                    <div className="text-base text-white/80 mb-1">Recap your day</div>
                    <div className="text-xs text-white/50 leading-relaxed">
                      HEXA will read everything you did today across Tasks, Mail, Meetings, Team Chat,
                      Attendance, Approvals, Files, and AI generations — then write a friendly summary you can save or delete.
                    </div>
                    <button
                      onClick={generate}
                      className="mt-5 btn-ghost text-xs flex items-center gap-1.5 px-4 py-2 mx-auto"
                      style={{ border: "1px solid rgba(139,92,246,0.5)", color: "#c7c1ff" }}
                    >
                      <Sparkles size={12} /> Generate today&apos;s recap
                    </button>
                  </div>
                </div>
              )}

              {generating && (
                <div className="h-full grid place-items-center text-white/60 text-sm">
                  <div className="flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Summarizing your day with HEXA AI…</div>
                </div>
              )}

              {shown && (
                <div className="max-w-3xl mx-auto">
                  <div className="flex items-baseline justify-between mb-3">
                    <div className="mono text-xs text-white/40 uppercase tracking-[0.2em]">
                      {shown.date}
                    </div>
                    {draft && (
                      <div className="mono text-[10px] uppercase tracking-widest text-amber-300/80">// draft · unsaved</div>
                    )}
                    {activeSaved && !draft && (
                      <div className="mono text-[10px] uppercase tracking-widest text-emerald-300/80">// saved</div>
                    )}
                  </div>

                  {draft ? (
                    <textarea
                      data-testid="recap-summary-editor"
                      value={editedSummary}
                      onChange={(e) => setEditedSummary(e.target.value)}
                      className="w-full min-h-[260px] bg-transparent border border-white/15 rounded p-4 outline-none text-sm leading-relaxed text-white/90 resize-y"
                      placeholder="Your day, summarized..."
                    />
                  ) : (
                    <div
                      data-testid="recap-summary-view"
                      className="text-sm leading-relaxed text-white/90 whitespace-pre-wrap p-4 rounded border border-white/10 bg-white/3"
                    >
                      {shown.summary}
                    </div>
                  )}

                  <ActivityBreakdown activities={shown.activities} />
                </div>
              )}
            </div>
          </main>
        </div>
      )}

      {tab === "team" && isBoss && (
        <div className="flex-1 flex flex-col min-h-0">
          {/* Team toolbar */}
          <div className="px-4 py-3 border-b border-white/10 flex items-center gap-3 flex-wrap">
            <Users size={16} className="text-[#3b82f6]" />
            <div className="mono text-xs uppercase tracking-[0.2em] text-white/80">Team Day Recap</div>
            <div className="flex items-center gap-2 ml-2">
              <Calendar size={12} className="text-white/40" />
              <input
                data-testid="team-recap-date"
                type="date"
                value={teamDate}
                max={todayStr()}
                onChange={(e) => setTeamDate(e.target.value)}
                className="bg-transparent border border-white/15 rounded px-2 py-1 text-xs"
              />
            </div>
            <label className="flex items-center gap-2 text-[11px] text-white/70 mono uppercase tracking-[0.18em] cursor-pointer">
              <input
                data-testid="team-recap-ai-toggle"
                type="checkbox"
                checked={teamAi}
                onChange={(e) => setTeamAi(e.target.checked)}
                className="accent-[#3b82f6]"
              />
              AI narrative
            </label>
            <button
              data-testid="team-recap-refresh"
              onClick={loadTeam}
              disabled={teamLoading}
              className="btn-ghost text-xs flex items-center gap-1.5 px-3 py-1.5"
              style={{ border: "1px solid rgba(59,130,246,0.5)", color: "#bfdbfe" }}
            >
              {teamLoading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
              {teamLoading ? "Loading…" : "Refresh"}
            </button>
          </div>

          {/* Totals strip */}
          {teamData && (
            <div className="px-4 py-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 border-b border-white/10">
              {[
                ["Team", teamData.totals.team_size, "#a78bfa"],
                ["Active today", teamData.totals.active_today, "#22c55e"],
                ["Tasks done", teamData.totals.tasks_completed, "#3b82f6"],
                ["Meetings", teamData.totals.meetings, "#f59e0b"],
                ["Mails sent", teamData.totals.mails_sent, "#06b6d4"],
                ["Files", teamData.totals.files_created, "#ec4899"],
              ].map(([label, n, color]) => (
                <div
                  key={label}
                  className="p-2.5 rounded border bg-white/3 flex flex-col gap-0.5"
                  style={{ borderColor: `${color}40` }}
                >
                  <div className="mono text-[9px] uppercase tracking-[0.22em] text-white/45">{label}</div>
                  <div className="text-lg mono font-medium" style={{ color }}>{n}</div>
                </div>
              ))}
            </div>
          )}

          {/* Employee list */}
          <div className="flex-1 overflow-y-auto p-4">
            {teamLoading && !teamData && (
              <div className="h-full grid place-items-center text-white/60 text-sm">
                <div className="flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading team activity…</div>
              </div>
            )}

            {teamData && teamData.items.length === 0 && (
              <div className="h-full grid place-items-center text-center text-white/50 text-sm">
                No employees in your workspace yet.
              </div>
            )}

            {teamData && teamData.items.length > 0 && (
              <div className="space-y-2.5 max-w-4xl mx-auto">
                {teamData.items.map((it) => {
                  const initial = (it.user.name || it.user.address)[0].toUpperCase();
                  const isOpen = !!expanded[it.user.address];
                  const isActive = it.score > 0;
                  return (
                    <div
                      key={it.user.address}
                      data-testid={`team-recap-card-${it.user.username}`}
                      className="rounded-lg border bg-white/3 overflow-hidden transition"
                      style={{ borderColor: isActive ? "rgba(59,130,246,0.3)" : "rgba(255,255,255,0.08)" }}
                    >
                      <button
                        onClick={() => setExpanded((m) => ({ ...m, [it.user.address]: !isOpen }))}
                        className="w-full px-4 py-3 flex items-start gap-3 hover:bg-white/3 text-left"
                      >
                        <div
                          className="grid place-items-center w-9 h-9 rounded-full text-sm font-semibold flex-shrink-0"
                          style={{
                            background: isActive
                              ? "linear-gradient(135deg, #a78bfa, #3b82f6)"
                              : "rgba(255,255,255,0.08)",
                            color: isActive ? "#fff" : "rgba(255,255,255,0.5)",
                          }}
                        >
                          {initial}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline gap-2 flex-wrap">
                            <div className="text-sm font-medium text-white/90 truncate">{it.user.name}</div>
                            <div className="mono text-[10px] text-white/40">{it.user.address}</div>
                            {it.user.role === "boss" && (
                              <span className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-[#a78bfa]/15 text-[#a78bfa]">boss</span>
                            )}
                          </div>
                          <div className="text-[12px] text-white/65 mt-1 leading-relaxed">
                            {it.quick}
                          </div>
                          {it.narrative && (
                            <div className="text-[12.5px] text-white/85 mt-2 leading-relaxed italic">
                              {it.narrative}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 mono text-[10px] text-white/50 flex-shrink-0">
                          <TrendingUp size={11} className={isActive ? "text-[#22c55e]" : "text-white/30"} />
                          {it.score}
                        </div>
                      </button>
                      {isOpen && (
                        <div className="px-4 pb-3 pt-1 border-t border-white/8">
                          <ActivityBreakdown activities={it.activities} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ActivityBreakdown({ activities }) {
  if (!activities) return null;
  const rows = [
    ["Tasks completed", (activities.tasks_completed || []).length],
    ["Tasks created", (activities.tasks_created || []).length],
    ["Meetings/Events", (activities.events || []).length],
    ["Mails sent", (activities.mail_sent || []).length],
    ["Team chat messages", activities.team_messages || 0],
    ["AI chat sessions", (activities.chat_sessions || []).length],
    ["Approvals decided", (activities.approvals_done || []).length],
    ["Files created", (activities.files_created || []).length],
    ["Images generated", activities.image_generations || 0],
  ];
  const total = rows.reduce((a, [, n]) => a + n, 0);
  if (total === 0 && !activities.attendance) return null;
  return (
    <div className="mt-3">
      <div className="mono text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">// activity</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {activities.attendance && (
          <div className="p-2.5 rounded border border-white/10 bg-white/3">
            <div className="text-[10px] mono text-white/40">Attendance</div>
            <div className="text-xs text-white/85">{activities.attendance.status || "—"}</div>
          </div>
        )}
        {rows.filter(([, n]) => n > 0).map(([label, n]) => (
          <div key={label} className="p-2.5 rounded border border-white/10 bg-white/3">
            <div className="text-[10px] mono text-white/40">{label}</div>
            <div className="text-xs text-white/85">{n}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
